import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import DeviceTypes from 'enum/device-type.enum';
import { shouldUseTizenTEPPlayer } from 'utils/device/tizenPlatform';
import { tLog, tWarn } from 'utils/device/tizenLog';
import SamsungPlayer from './SamsungPlayer';
import TizenPlayer from './TizenPlayer';
import TizenTEPPlayer from './TizenTEPPlayer';
import WebOSPlayer from './WebOSPlayer';
import AndroidPlayer from './AndroidPlayer';
import DefaultPlayer from './DefaultPlayer';

/**
 * Factory for creating device-specific player instances
 * Determines device type and returns appropriate player implementation
 */

// 🧪 TESTING FLAG: Can be controlled via .env with REACT_APP_FORCE_TIZEN_PLAYER=true
const FORCE_TIZEN_PLAYER_FOR_TESTING = process.env.REACT_APP_FORCE_TIZEN_PLAYER === 'true';

let cachedPlayer = null;
let cachedDeviceType = null;

/**
 * Get current device type from storage
 * @returns {string} Device type from DeviceTypes enum
 */
export const getDeviceType = () => {
  // 🧪 TESTING: Force Tizen player for browser testing
  if (FORCE_TIZEN_PLAYER_FOR_TESTING) {
    console.warn('⚠️ TESTING MODE: Forcing TizenPlayer in browser');
    return DeviceTypes.TIZEN;
  }

  const deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
  tLog('[Factory]', 'flags: TEP=' + deviceList.isTIZEN_TEP + ' legacy=' + deviceList.isTIZEN +
    ' SAMSUNG=' + deviceList.isSAMSUNG + ' WEBOS=' + deviceList.isWEBOS + ' ANDROID=' + deviceList.isANDROID);
  if (deviceList.isSAMSUNG) return DeviceTypes.SAMSUNG;
  if (deviceList.isTIZEN_TEP) {
    tLog('[Factory]', '✅ routing → TIZEN_TEP player');
    return DeviceTypes.TIZEN_TEP;
  }
  // BU8000 (Tizen 6.5) was misclassified as legacy Tizen — correct via runtime API check
  if (deviceList.isTIZEN && shouldUseTizenTEPPlayer()) {
    tWarn('[Factory]', '⚠️ correcting legacy→TIZEN_TEP (runtime avplay check)');
    return DeviceTypes.TIZEN_TEP;
  }
  if (deviceList.isTIZEN) {
    tLog('[Factory]', '✅ routing → TIZEN legacy player');
    return DeviceTypes.TIZEN;
  }
  if (deviceList.isWEBOS) return DeviceTypes.WEBOS;
  if (deviceList.isANDROID) return DeviceTypes.ANDROID;
  return DeviceTypes.DEFAULT;
};

/**
 * Create or return cached device player instance
 * Players are singletons per device type
 * @param {string} deviceTypeOverride - Optional device type override
 * @returns {BasePlayer} Device-specific player instance
 */
export const createDevicePlayer = (deviceTypeOverride = null) => {
  const deviceType = deviceTypeOverride || getDeviceType();

  // Return cached player if device type matches
  if (cachedPlayer && cachedDeviceType === deviceType) {
    return cachedPlayer;
  }

  // Create new player based on device type
  switch (deviceType) {
    case DeviceTypes.SAMSUNG:
      cachedPlayer = new SamsungPlayer();
      break;

    case DeviceTypes.TIZEN:
      cachedPlayer = new TizenPlayer();
      break;

    case DeviceTypes.TIZEN_TEP:
      cachedPlayer = new TizenTEPPlayer();
      break;

    case DeviceTypes.WEBOS:
      cachedPlayer = new WebOSPlayer();
      break;

    case DeviceTypes.ANDROID:
      cachedPlayer = new AndroidPlayer();
      break;

    default:
      cachedPlayer = new DefaultPlayer();
      break;
  }

  cachedDeviceType = deviceType;
  tLog('[Factory]', '✅ player created: ' + deviceType);
  console.log(`Device player created: ${deviceType}`);

  return cachedPlayer;
};

/**
 * Clear cached player (useful for testing or device changes)
 */
export const clearPlayerCache = () => {
  cachedPlayer = null;
  cachedDeviceType = null;
};
